/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { debounce, omit } from 'lodash';
import qs from 'qs';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import type { useRetracked } from 'js/lib/retracked';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { VisuallyHidden, breakpoints } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import MagnifierSvg from 'bundles/browse/components/PageHeader/MagnifierSvg';
import type { DiscoveryCollection } from 'bundles/browse/components/types/DiscoveryCollections';
import useIsLihpHome from 'bundles/collections-carousel/utils/useIsLihp';
import useIsLohp from 'bundles/collections-common/utils/useIsLohp';
import {
  getShouldShowUnregisteredOnboarding,
  previewShouldShowUnregisteredOnboarding,
} from 'bundles/coursera-plus/utils/subscriptionsForwardDiscoveryExperiment';
import HeroBannerSearchBar from 'bundles/front-page/components/modules/hero-banner/HeroBannerSearchBar';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import PageHeaderContext from 'bundles/page-header/contexts/PageHeaderContext';
import TrackedButton from 'bundles/page/components/TrackedButton';
import AutoCompleteAriaSuggestions from 'bundles/search-common/components/searchbar/AutoCompleteAriaSuggestions';
import AutoCompleteInput from 'bundles/search-common/components/searchbar/AutoCompleteInput';
import {
  MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR,
  ON_SITE_SEARCH_PATH,
  SEARCH_AUTOCOMPLETE_MAIN_INDEX_ID,
  SEARCH_BAR_LOCATION,
  SEARCH_SUGGESTIONS_INDEX_ID,
} from 'bundles/search-common/constants';
import type { SearchBarLocation } from 'bundles/search-common/constants';
import { ARIA_CONTENT_DEBOUNCE_DURATION_MS, DEBOUNCE_DURATION_MS } from 'bundles/search-common/constants/autocomplete';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { AutocompleteSection, Hits, SuggestionHits } from 'bundles/search-common/types/autocomplete';
import { getOnboardingAutocompleteSection } from 'bundles/search-common/utils/SearchUtils';
import { getAutoCompleteSections, getAutocompleteLegacyEventingData } from 'bundles/search-common/utils/autocomplete';
import { getSuggestionsSearchEngine } from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import { removeWhiteSpace } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

export type Props = {
  isSearchPage?: boolean;
  pageLocation?: SearchBarLocation;
  searchIsOpen?: boolean;
  collectionRecommendations?: DiscoveryCollection | null;
  query: string;
  onSearchFocus?: () => void;
  hideMobileSearchPage?: () => void;
  shouldFocusSearch?: boolean;
  enableOneStepSearch?: boolean;
  shouldShowExposedSearchAndReg?: boolean;
  track: ReturnType<typeof useRetracked>;
};

const styles = {
  preUnifiedAutoComplete: css`
    /* As per PageHeader.styl, mobile header kicks in at 1060px; these styles are for desktop only */
    @media (min-width: 1061px) {
      .react-autosuggest__container > input {
        border-radius: 24px !important;
        padding: var(--cds-spacing-100) var(--cds-spacing-200) !important;
        border: 1px solid var(--design-sprint-gray-4, #dbe0e1) !important;
        color: var(--design-sprint-gray-2, #63676b) !important;
        line-height: 24px !important;
        height: 44px !important;
      }

      .search-button .magnifier-wrapper {
        right: 3px;
        border-radius: 24px;
        width: 32px;
        height: 32px;
        padding: var(--cds-spacing-100);
        margin-right: var(--cds-spacing-50);
        margin-top: 6px;
        border: 1px solid transparent;
      }
    }
  `,
  onboardingAutocompleteItem: (sectionsLength: number) => css`
    .react-autosuggest__suggestions-list {
      padding-left: 0;
    }
    #react-autowhatever-1-section-${sectionsLength - 1}-item-0.react-autosuggest__suggestion--highlighted {
      background-color: transparent;
    }
    #react-autowhatever-1-section-${sectionsLength - 1}-item-0 {
      padding: 0;
      border-top: 1px solid #e0e0e0;
      padding-top: var(--cds-spacing-200);
      list-style-type: none;
      ${breakpoints.down('sm')} {
        margin: var(--cds-spacing-200);
      }
    }
  `,
};

type AriaContent = ({ sectionTitle?: string | boolean; hits?: SuggestionHits[] } & Hits)[];

export const AutoComplete = (props: Props) => {
  const {
    enableOneStepSearch = false,
    shouldShowExposedSearchAndReg = false,
    pageLocation,
    isSearchPage = false,
    track,
    collectionRecommendations,
    query,
    shouldFocusSearch,
    searchIsOpen,
    hideMobileSearchPage,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const tracker = useTracker();
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<AutocompleteSection[]>([]);
  const [previousHits, setPreviousHits] = useState<(SuggestionHits | undefined)[]>([]);
  const [isFocused, setIsFocused] = useState(shouldFocusSearch || false);
  const ariaTimeoutRef = useRef<NodeJS.Timeout>();
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const [ariaContent, setAriaContent] = useState<AriaContent>([]);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState<string>('');
  const [shouldUpdateAriaAfterLoading, setShouldUpdateAriaAfterLoading] = useState(false);
  // currentText may evolve separately of the current refinement due to debouncing and is the
  // source of truth to what the autocomplete box displays.
  const [currentText, setCurrentText] = useState<string>(query || '');
  const autoCompleteInput = useRef<HTMLInputElement>();
  const { getSearchResults, updateQuery, isLoading } = useSearch();
  const searchData = getSearchResults(SEARCH_AUTOCOMPLETE_MAIN_INDEX_ID);
  const { elements, source, query: searchDataQuery } = searchData || {};
  const indexName = source?.indexName;
  const suggestionsData = getSearchResults(SEARCH_SUGGESTIONS_INDEX_ID);
  const { elements: suggestionElements } = suggestionsData || {};
  const autocompleteSuggestionsAvailable = (elements?.length || 0) + (suggestionElements?.length || 0) > 0;
  const page = window?.location.pathname;

  const referrer = location.search ? `${location.pathname}${location.search}` : location.pathname;
  const { onboardingUrl } = useBuildOnboardingUrl({ referrer });
  const onboardingAutocompleteSection = useMemo(() => getOnboardingAutocompleteSection(onboardingUrl), [onboardingUrl]);
  const showOnboardingSuggestion = previewShouldShowUnregisteredOnboarding();
  const addOnboardingStyles = showOnboardingSuggestion
    ? styles.onboardingAutocompleteItem(autocompleteSuggestions.length)
    : undefined;

  // running the autocomplete experiment on these pages
  const isLOHP = useIsLohp();
  const isConsumerSERP = useIsConsumerSerp();
  const isEQP = useIsEqp();
  const isLIHPHome = useIsLihpHome();
  const isValidSearchSuggestionExperimentPage = isLOHP || isConsumerSERP || isEQP || isLIHPHome;

  if (isFocused) {
    getShouldShowUnregisteredOnboarding();
    getSuggestionsSearchEngine(isValidSearchSuggestionExperimentPage);
  }

  const onSuggestionHighlighted = useCallback<(params: { suggestion: SuggestionHits }) => void>(
    ({ suggestion }: { suggestion: SuggestionHits }) => setHighlightedSuggestion(suggestion?.name || ''),
    []
  );

  const debouncedAriaTextUpdate = useCallback<() => void>(() => {
    clearTimeout(ariaTimeoutRef.current);
    if (ariaContent.length > 0) setAriaContent([]);
    ariaTimeoutRef.current = setTimeout(() => setShouldUpdateAriaAfterLoading(true), ARIA_CONTENT_DEBOUNCE_DURATION_MS);
  }, [ariaContent.length]);

  const memoizedAutoCompleteSections = useMemo(
    () => getAutoCompleteSections(searchDataQuery || '', searchData, suggestionsData, collectionRecommendations),
    [searchDataQuery, searchData, suggestionsData, collectionRecommendations]
  );

  useEffect(() => {
    if (shouldUpdateAriaAfterLoading && !isLoading) {
      setAriaContent(memoizedAutoCompleteSections);
      setShouldUpdateAriaAfterLoading(false);
    }
  }, [autocompleteSuggestions, isLoading, memoizedAutoCompleteSections, shouldUpdateAriaAfterLoading]);

  const refineAndTrack = useCallback<(newValue: string) => void>(
    (newValue: string) => {
      track({
        action: 'search',
        trackingName: 'algolia_search_input_change',
        trackingData: { newSearchValue: removeWhiteSpace(newValue), page: window?.location.pathname, indexName },
      });
      updateQuery(newValue);
    },
    [track, updateQuery, indexName]
  );
  const debouncedRefineAndTrack = useCallback<(newQuery: string) => void>(
    (newQuery: string) => {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        refineAndTrack(newQuery);
      }, DEBOUNCE_DURATION_MS);
    },
    [refineAndTrack]
  );

  const trackAutocompleteViews = useCallback<(hits: (SuggestionHits | undefined)[], query: string) => void>(
    (hits: (SuggestionHits | undefined)[], query: string) => {
      hits?.forEach((hit) => {
        if (!hit) {
          return;
        }
        if (hit.objectID === 'onboarding_link') {
          tracker('view_button', {
            button: {
              name: 'start_onboarding',
            },
          });
        }
        const results = getAutocompleteLegacyEventingData(hit, query);
        tracker('view_autocomplete_suggestions', results);
      });
    },
    [tracker]
  );
  const debounceTrackAutocompleteViews = useMemo(
    () =>
      debounce((hits: (SuggestionHits | undefined)[]) => {
        trackAutocompleteViews(hits, currentText);
      }, DEBOUNCE_DURATION_MS),
    [currentText, trackAutocompleteViews]
  );

  const blurAutocomplete = () => {
    autoCompleteInput?.current?.blur();
    setIsFocused(false);
    hideMobileSearchPage?.();
  };

  useEffect(() => {
    if (query && typeof window !== 'undefined') {
      debouncedRefineAndTrack(query);
    }

    // remove default a11y attributes
    const autocompleteContainerElement = document?.getElementsByClassName('react-autosuggest__container')?.[0];
    if (autocompleteContainerElement) {
      autocompleteContainerElement.removeAttribute('role');
      autocompleteContainerElement.removeAttribute('aria-haspopup');
    }

    return () => {
      clearTimeout(ariaTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (currentText !== query) {
      // Because we update the query via the SuggestedSearches component, we want to update the search bar query as well
      // when it is updated that way, so we check if the url has changed & if the current search bar text did not, then it means
      // it was updated there.
      setCurrentText(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (window?.innerWidth <= MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR) {
      const notOnSearchOrFrontPage =
        !isSearchPage &&
        pageLocation !== SEARCH_BAR_LOCATION.FrontPage &&
        pageLocation !== SEARCH_BAR_LOCATION.FrontPageBanner;

      if (searchIsOpen) {
        setIsFocused(true);
        autoCompleteInput?.current?.focus();
      } else if (!searchIsOpen && notOnSearchOrFrontPage) {
        setIsFocused(false);
        autoCompleteInput?.current?.blur();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  useEffect(() => {
    // There's need for this because change in page width to desktop size while the mobile search bar is open
    // makes it invisible but doesn't fully effect hideMobileSearchPage which then makes the page navigation sticky
    const hideMobileSearchPageOnScreenResize = () => {
      if (window?.innerWidth > MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR) {
        blurAutocomplete();
      }
    };

    window.addEventListener('resize', hideMobileSearchPageOnScreenResize, { passive: true });
    return () => {
      window.removeEventListener('resize', hideMobileSearchPageOnScreenResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // optimization for reducing calculation when loading
    if (!isLoading) {
      if (showOnboardingSuggestion) {
        setAutocompleteSuggestions([...memoizedAutoCompleteSections, onboardingAutocompleteSection]);
      } else {
        setAutocompleteSuggestions(memoizedAutoCompleteSections);
      }
    }
  }, [isLoading, memoizedAutoCompleteSections, showOnboardingSuggestion, onboardingAutocompleteSection]);

  useEffect(() => {
    const hits = autocompleteSuggestions.map((suggestion) => suggestion.hits).flat();
    const hitsHaveChanged = JSON.stringify(hits) !== JSON.stringify(previousHits);

    if (isFocused && hitsHaveChanged) {
      setPreviousHits(hits);
      debounceTrackAutocompleteViews(hits);
    }
  }, [currentText, autocompleteSuggestions, isFocused, debounceTrackAutocompleteViews, previousHits]);

  const autoCompleteClass = classNames('rc-AutoComplete horizontal-box isLohpRebrand', {
    isFocused,
    isExposedSearch: shouldShowExposedSearchAndReg,
    isOneStepSearchForTablet: enableOneStepSearch && !shouldShowExposedSearchAndReg,
  });

  const setCursorAtEndOfString = () => {
    // If component should auto focus and has a starting query value, put cursor at end of query string.
    // Otherwise cursor will be placed at beginning.
    if (query && shouldFocusSearch) {
      autoCompleteInput?.current?.setSelectionRange(query.length, query.length);
    }
  };

  const storeInputReference = (autosuggest: { input: HTMLInputElement | undefined } | null) => {
    if (autosuggest !== null) {
      autoCompleteInput.current = autosuggest.input;
      setCursorAtEndOfString();
    }
  };

  const clearSearchQueryAndCloseContainer = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setCurrentText('');
    blurAutocomplete();
  };

  const searchButtonOnClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const otherQueryParams = omit(location.query, 'query');
    const otherQueryParamsString = qs.stringify(otherQueryParams);
    const urlWithQuery = `${ON_SITE_SEARCH_PATH}?query=${encodeURIComponent(currentText)}&${otherQueryParamsString}`;
    const newSearchProviderUrl = `${ON_SITE_SEARCH_PATH}?query=${encodeURIComponent(currentText)}`;

    if (isSearchPage) {
      navigate(newSearchProviderUrl);
    } else {
      window?.location.assign(urlWithQuery);
    }

    tracker('perform_search', {
      searchedQuery: currentText,
    });

    track({
      action: 'search',
      trackingName: 'autocomplete_query',
      trackingData: { searchText: currentText, page, autocompleteSuggestionsAvailable, indexName },
    });
    track({
      action: 'search',
      trackingName: 'algolia_search_page_query_updated',
      trackingData: { searchText: currentText, page, indexName },
    });
    blurAutocomplete();
  };

  const autocompleteInputProps = {
    ...props,
    autocompleteSuggestions,
    storeInputReference,
    onSuggestionHighlighted,
    blurAutocomplete,
    currentText,
    setCurrentText,
    autocompleteSuggestionsAvailable,
    setIsFocused,
    isFocused,
    debouncedAriaTextUpdate,
    debouncedRefineAndTrack,
  };

  return (
    <PageHeaderContext.Consumer>
      {({ isSimplifiedPageHeader }) => (
        <div
          data-testid="AutoComplete"
          className={autoCompleteClass}
          style={isFocused ? { zIndex: 4001 } : {}}
          {...(isSimplifiedPageHeader
            ? {
                css: [styles.preUnifiedAutoComplete, addOnboardingStyles],
              }
            : {
                css: addOnboardingStyles,
              })}
        >
          {pageLocation === SEARCH_BAR_LOCATION.FrontPage || pageLocation === SEARCH_BAR_LOCATION.FrontPageBanner ? (
            <HeroBannerSearchBar
              searchButtonOnClick={searchButtonOnClick}
              isFocused={isFocused}
              pageLocation={pageLocation}
            >
              <AutoCompleteInput {...autocompleteInputProps} />
            </HeroBannerSearchBar>
          ) : (
            <>
              <button
                // NOTE: Mobile Button - hidden via css
                type="button"
                className="nostyle mobile-magnifier left-hand-search-btn"
                aria-label={_t('Submit Search')}
                // Tracking it programatically to pass the query data
                onMouseDown={searchButtonOnClick}
                onKeyPress={(event) => a11yKeyPress(event, searchButtonOnClick)}
              >
                <MagnifierSvg fill="#E1E1E1" />
              </button>
              <AutoCompleteInput {...autocompleteInputProps} />
              <button
                type="button"
                className="nostyle search-button"
                aria-label={_t('Submit Search')}
                // Tracking it programatically to pass the query data
                onClick={searchButtonOnClick}
                onKeyPress={(event) => a11yKeyPress(event, searchButtonOnClick)}
              >
                <MagnifierSvg fill="#E1E1E1" />
              </button>
            </>
          )}

          <VisuallyHidden>
            <div aria-live="polite" aria-atomic="true" id="autocompleteARIASuggestions" role="region">
              {!highlightedSuggestion && (
                <AutoCompleteAriaSuggestions isFocused={isFocused} isLoading={isLoading} ariaContent={ariaContent} />
              )}
            </div>
          </VisuallyHidden>
          {isFocused && (
            <TrackedButton
              trackingName="close_auto_complete"
              className="nostyle cancel-button"
              aria-label={_t('Close Search Auto Complete')}
              data-testid="close_auto_complete"
              onMouseDown={clearSearchQueryAndCloseContainer}
              onKeyPress={(event) => a11yKeyPress(event, clearSearchQueryAndCloseContainer)}
            >
              <CloseIcon color="interactive" size="medium" />
            </TrackedButton>
          )}
        </div>
      )}
    </PageHeaderContext.Consumer>
  );
};

export default React.memo(AutoComplete);
